import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, IconButton, Collapse, Divider, Button,
    Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import supabase from '../../../../supabaseClient';
import { computeProductionSessions, PRODUCTION_DISPLAY } from '../../../../Utils/machineProductionStats';

dayjs.extend(isoWeek);

const WEEKDAY_SHORT = ['nie', 'pon', 'wt', 'śr', 'czw', 'pt', 'sob'];
const MONTH_NAMES = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

const GREEN = '#1B7A3D';
const pad = (n) => String(n).padStart(2, '0');

const fmtNum = (v, digits = 1) =>
    (v || 0).toLocaleString('pl-PL', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtPln = (v) => `${Math.round(v || 0).toLocaleString('pl-PL')} zł`;
const fmtTime = (iso) => (iso ? dayjs(iso).format('HH:mm') : '—');
const fmtHM = (minutes) => `${Math.floor((minutes || 0) / 60)}h ${pad(Math.round((minutes || 0) % 60))}min`;

function getPeriodRange(mode, anchor) {
    const a = dayjs(anchor);
    if (mode === 'week') return { start: a.startOf('isoWeek'), end: a.endOf('isoWeek') };
    return { start: a.startOf('month'), end: a.endOf('month') };
}

function shiftAnchor(mode, anchor, direction) {
    const a = dayjs(anchor);
    if (mode === 'week') return a.add(direction, 'week');
    return a.add(direction, 'month');
}

function formatRangeLabel(mode, start, end) {
    if (mode === 'week') return `tydzień ${start.format('DD')} – ${end.format('DD.MM.YYYY')}`;
    return `${MONTH_NAMES[start.month()]} ${start.format('YYYY')}`;
}

// czas trwania sesji w minutach; dla otwartej sesji liczony do "teraz"
function sessionMinutes(s) {
    const end = s.endTime ? dayjs(s.endTime) : dayjs();
    return Math.max(0, end.diff(dayjs(s.startTime), 'minute', true));
}

function FilterPill({ active, onClick, children }) {
    return (
        <Button
            onClick={onClick}
            variant={active ? 'contained' : 'outlined'}
            disableElevation
            size="small"
            sx={{
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.3px',
                px: 1.5,
                py: 0.6,
                color: active ? 'primary.contrastText' : 'primary.main',
                backgroundColor: active ? 'primary.main' : 'background.paper',
                borderColor: active ? 'primary.main' : 'secondary.light',
                '&:hover': {
                    backgroundColor: active ? 'primary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                },
            }}
        >
            {children}
        </Button>
    );
}

function StatCard({ label, value, color, hint }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 220,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'secondary.light',
                borderTop: '3px solid',
                borderTopColor: color,
                borderRadius: '12px',
                px: 2.5,
                py: 2,
            }}
        >
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Box sx={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1.3, mt: 0.5 }}>
                {value}
            </Box>
            {hint && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
}

// lista "nazwa maszyny — wartość" używana w kartach statystyk, gdy w okresie
// wystąpiła produkcja na więcej niż jednej maszynie (różne jednostki, więc nie
// da się tego pokazać jako jedną zsumowaną liczbę)
function MachineBreakdownList({ rows }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {rows.map((r) => (
                <Box key={r.deviceId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                    <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' }}>
                        {r.deviceName}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                        {r.text}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// pasek produkcji tygodnia z linią oznaczającą normę; skala dopasowana do większej
// z dwóch wartości, żeby przekroczenie normy nadal mieściło się w pasku
function ProductionBar({ production, norma }) {
    const scaleMax = Math.max(production, norma, 0.0001);
    const fillPercent = Math.min(100, (production / scaleMax) * 100);
    const markerPercent = norma > 0 ? Math.min(100, (norma / scaleMax) * 100) : null;
    const achieved = norma > 0 && production >= norma;

    return (
        <Box sx={{ position: 'relative', height: 10, borderRadius: 5, backgroundColor: '#EDE6DE', overflow: 'hidden' }}>
            <Box
                sx={{
                    position: 'absolute', top: 0, left: 0, bottom: 0,
                    width: `${fillPercent}%`,
                    backgroundColor: achieved ? GREEN : '#4C9A63',
                    borderRadius: 5,
                    transition: 'width 0.2s ease',
                }}
            />
            {markerPercent != null && (
                <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${markerPercent}%`, width: '2px', backgroundColor: 'primary.main' }} />
            )}
        </Box>
    );
}

// pasek + podsumowanie produkcji jednej maszyny w ramach tygodnia; gdy w tygodniu
// pracowała tylko jedna maszyna, nagłówek to po prostu "Produkcja" (jak dawniej),
// a przy kilku maszynach każda dostaje własną nazwę, własną normę i własną jednostkę.
// Komunikat jest dwustanowy: albo "zostało do premii X" (norma nieosiągnięta),
// albo "przekroczono normę o X" (norma osiągnięta/przekroczona).
function MachineProductionSummary({ machine, showDeviceName }) {
    const fmtQty = (v) => `${fmtNum(v, machine.decimals)} ${machine.unit}`;
    const remaining = Math.max(0, machine.norma - machine.production);

    return (
        <Box sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{
                    fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                    color: showDeviceName ? 'primary.main' : 'text.secondary',
                }}>
                    {showDeviceName ? machine.deviceName : 'Produkcja'}
                </Typography>
                {machine.norma > 0 && (
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>norma {fmtQty(machine.norma)}</Typography>
                )}
            </Box>
            <ProductionBar production={machine.production} norma={machine.norma} />
            <Typography sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 700, color: GREEN }}>{fmtQty(machine.production)}</Box>
                {' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                    {machine.norma > 0 && (
                        machine.overNorm > 0.005
                            ? `przekroczono normę o ${fmtQty(machine.overNorm)} · `
                            : `zostało do premii ${fmtQty(remaining)} · `
                    )}
                    {machine.percentOfNorm != null ? `${Math.round(machine.percentOfNorm)}% normy tygodnia` : ''}
                </Box>
            </Typography>
        </Box>
    );
}

// rozwinięty szczegół jednego pracownika: dzień po dniu w ramach tygodnia —
// ile odbić, kiedy, i ile wyprodukowano w każdym z nich
function WorkerDayDetail({ worker, fmtQty }) {
    return (
        <Box sx={{ py: 1.5, px: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {worker.days.map((d) => {
                const dd = dayjs(d.dateStr);
                return (
                    <Box key={d.dateStr} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, minWidth: 90, color: 'text.secondary' }}>
                            {WEEKDAY_SHORT[dd.day()]} {dd.format('DD.MM')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 180 }}>
                            {d.sessions.map((s, i) => (
                                <Typography key={i} sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                    {fmtTime(s.startTime)} → {s.isOpen ? 'w toku' : fmtTime(s.endTime)}
                                    {s.quantity != null ? ` · ${fmtQty(s.quantity)}` : ''}
                                </Typography>
                            ))}
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, minWidth: 70, textAlign: 'right' }}>
                            {fmtQty(d.quantity)}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

// tabela pracowników jednej maszyny w ramach tygodnia — jednostka i precyzja są
// tej konkretnej maszyny, więc nigdy nie mieszają się ze sobą. Wiersz pracownika
// rozwija się, pokazując historię odbić i produkcji dzień po dniu (WorkerDayDetail).
function MachineWorkersTable({ machine, showDeviceName }) {
    const [expanded, setExpanded] = useState(() => new Set());
    const fmtQty = (v) => `${fmtNum(v, machine.decimals)} ${machine.unit}`;
    const toggle = (employeeId) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(employeeId)) next.delete(employeeId); else next.add(employeeId);
            return next;
        });
    };

    return (
        <Box sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
            {showDeviceName && (
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.main', mb: 1 }}>
                    {machine.deviceName}
                </Typography>
            )}
            <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 32, border: 0 }} />
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', pl: 0, border: 0 }}>Pracownik</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }}>Odbicia na maszynie</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }}>Czas</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }}>Udział</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }} align="right">Produkcja</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }} align="right">Ponad normę</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem', border: 0 }} align="right">Premia</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {machine.workers.map((w) => {
                        const isOpen = expanded.has(w.employeeId);
                        return (
                            <React.Fragment key={w.employeeId}>
                                <TableRow
                                    hover
                                    onClick={() => toggle(w.employeeId)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={{ border: 0, py: 1 }}>
                                        <IconButton size="small" sx={{ p: 0.25 }}>
                                            {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ pl: 0, border: 0, py: 1 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{w.employeeName}</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{w.standName}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ border: 0, fontSize: '0.85rem', color: 'text.secondary' }}>
                                        {fmtTime(w.startTime)} → {w.isOpen ? 'w toku' : fmtTime(w.endTime)}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, fontSize: '0.85rem' }}>{fmtHM(w.minutes)}</TableCell>
                                    <TableCell sx={{ border: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 60, height: 6, borderRadius: 3, backgroundColor: 'secondary.light', overflow: 'hidden' }}>
                                                <Box sx={{ width: `${Math.min(100, Math.round(w.share))}%`, height: '100%', backgroundColor: 'primary.main' }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.8rem' }}>{Math.round(w.share)}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={{ border: 0, fontSize: '0.85rem' }}>{fmtQty(w.production)}</TableCell>
                                    <TableCell align="right" sx={{ border: 0, fontSize: '0.85rem', color: w.overNorm > 0.005 ? GREEN : 'text.secondary' }}>
                                        {fmtQty(w.overNorm)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ border: 0, fontWeight: 700, fontSize: '0.9rem', color: GREEN }}>
                                        {fmtPln(w.premium)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ border: 0, p: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '8px' }}>
                                                <WorkerDayDetail worker={w} fmtQty={fmtQty} />
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}

// rozkład produkcji dzień-po-dniu w ramach tygodnia — czysto informacyjny
// wgląd w rozkład pracy; norma/premia istnieją już tylko na poziomie tygodnia
function DailyBreakdownList({ machine }) {
    const fmtQty = (v) => `${fmtNum(v, machine.decimals)} ${machine.unit}`;
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            {machine.dailyBreakdown.map((d) => {
                const dd = dayjs(d.dateStr);
                return (
                    <Box key={d.dateStr} sx={{ textAlign: 'center', px: 1, py: 0.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '8px', minWidth: 60 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
                            {WEEKDAY_SHORT[dd.day()]} {dd.format('DD.MM')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{fmtQty(d.quantity)}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

function WeekCard({ week, isOpen, onToggle }) {
    const label = `${dayjs(week.weekStart).format('DD.MM')} – ${dayjs(week.weekEnd).format('DD.MM.YYYY')}`;
    const achieved = week.machines.some((m) => m.norma > 0 && m.production >= m.norma);
    const showDeviceNames = week.machines.length > 1;

    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'secondary.light',
                borderLeft: '4px solid',
                borderLeftColor: achieved ? GREEN : 'secondary.light',
                borderRadius: '12px',
                mb: 1.5,
                overflow: 'hidden',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2.5, py: 2, flexWrap: 'wrap', cursor: 'pointer' }}
            >
                <Box sx={{ minWidth: 150 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.main' }}>tydzień {label}</Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 260 }}>
                    {week.machines.map((m) => (
                        <MachineProductionSummary key={m.deviceId} machine={m} showDeviceName={showDeviceNames} />
                    ))}
                </Box>

                <Box sx={{ textAlign: 'right', minWidth: 110 }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Premia tygodnia
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: GREEN }}>{fmtPln(week.premium)}</Typography>
                </Box>

                <IconButton
                    sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '8px', pointerEvents: 'none' }}
                >
                    {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </Box>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box sx={{ borderTop: '1px solid', borderColor: 'secondary.light', px: 2.5, py: 2, backgroundColor: 'rgba(0,0,0,0.015)', overflowX: 'auto' }}>
                    {week.machines.map((m) => (
                        <Box key={m.deviceId} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                            {showDeviceNames && (
                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.main', mb: 1 }}>
                                    {m.deviceName}
                                </Typography>
                            )}
                            <DailyBreakdownList machine={m} />
                            <MachineWorkersTable machine={m} showDeviceName={false} />
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
}

function WorkerOperationReport() {
    const [idConfiguration, setIdConfiguration] = useState('');
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null); // null = wszystkie maszyny

    const [periodMode, setPeriodMode] = useState('week');
    const [anchor, setAnchor] = useState(dayjs().format('YYYY-MM-DD'));

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openWeek, setOpenWeek] = useState(null);

    useEffect(() => {
        const init = async () => {
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) return;
            const userId = sessionData.session.user.id;
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('id_configuration')
                .eq('id', userId)
                .single();
            if (profileError || !profileData) {
                console.log('Błąd pobierania profilu:', profileError);
                return;
            }
            setIdConfiguration(profileData.id_configuration);
        };
        init();
    }, []);

    useEffect(() => {
        if (!idConfiguration) return;
        const fetchDevices = async () => {
            const { data, error } = await supabase
                .from('devices')
                .select('id, name, daily_target, unit, production_source')
                .eq('id_configuration', idConfiguration)
                .is('archived', null)
                .not('production_source', 'is', null)
                .order('id', { ascending: true });
            if (error) {
                console.log('Błąd pobierania maszyn:', error);
                return;
            }
            setDevices(data || []);
        };
        fetchDevices();
    }, [idConfiguration]);

    const { start, end } = useMemo(() => getPeriodRange(periodMode, anchor), [periodMode, anchor]);
    const dateFrom = start.format('YYYY-MM-DD');
    const dateTo = end.format('YYYY-MM-DD');

    useEffect(() => {
        if (!idConfiguration) return;
        let ignore = false;
        setLoading(true);
        computeProductionSessions(supabase, {
            idConfiguration, dateFrom, dateTo, deviceId: selectedDeviceId,
        }).then((rows) => {
            if (ignore) return;
            setSessions(rows);
            setLoading(false);
        });
        return () => { ignore = true; };
    }, [idConfiguration, dateFrom, dateTo, selectedDeviceId]);

    useEffect(() => {
        setOpenWeek(null);
    }, [dateFrom, dateTo, selectedDeviceId]);

    // grupowanie sesji po tygodniu ISO, a w ramach tygodnia po maszynie i po
    // pracowniku — maszyny mają różne jednostki (m3 vs szt.), więc produkcji nie
    // da się sumować między nimi, trzeba trzymać osobno aż do wyświetlenia.
    // Norma/premia istnieją teraz tylko na poziomie tygodnia (weeklyTarget/
    // weeklyUnitsOverNorma/weeklyValue z computeProductionSessions) — dzień jest
    // już tylko informacyjnym rozbiciem (patrz dailyBreakdown).
    const weekGroups = useMemo(() => {
        const byWeek = {};
        sessions.forEach((s) => {
            (byWeek[s.weekKey] = byWeek[s.weekKey] || []).push(s);
        });

        return Object.values(byWeek)
            .map((weekSessions) => {
                const byDevice = {};
                weekSessions.forEach((s) => {
                    (byDevice[s.deviceId] = byDevice[s.deviceId] || []).push(s);
                });

                const machines = Object.values(byDevice).map((devSessions) => {
                    const first = devSessions[0];
                    const device = devices.find((dev) => dev.id === first.deviceId);
                    const display = PRODUCTION_DISPLAY[device?.production_source]
                        || { unit: device?.unit || first.unit || 'm³', decimals: 1 };

                    const production = devSessions.reduce((sum, s) => sum + (s.quantity || 0), 0);
                    const norma = first.weeklyTarget != null ? first.weeklyTarget : 0;
                    const overNorm = Math.max(0, production - norma);
                    const percentOfNorm = norma > 0 ? (production / norma) * 100 : null;

                    const byDay = {};
                    devSessions.forEach((s) => {
                        const dateStr = dayjs(s.startTime).format('YYYY-MM-DD');
                        byDay[dateStr] = (byDay[dateStr] || 0) + (s.quantity || 0);
                    });
                    const dailyBreakdown = Object.entries(byDay)
                        .map(([dateStr, quantity]) => ({ dateStr, quantity }))
                        .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

                    const byEmployee = {};
                    devSessions.forEach((s) => {
                        (byEmployee[s.employeeId] = byEmployee[s.employeeId] || []).push(s);
                    });
                    const totalMinutesDevice = devSessions.reduce((sum, s) => sum + sessionMinutes(s), 0);

                    const workers = Object.values(byEmployee).map((empSessions) => {
                        const emp = empSessions[0];
                        const starts = empSessions.map((s) => dayjs(s.startTime));
                        const minStart = starts.reduce((a, b) => (a.isBefore(b) ? a : b));
                        const hasOpen = empSessions.some((s) => s.isOpen);
                        const closedEnds = empSessions.filter((s) => s.endTime).map((s) => dayjs(s.endTime));
                        const maxEnd = closedEnds.length ? closedEnds.reduce((a, b) => (a.isAfter(b) ? a : b)) : null;
                        const minutes = empSessions.reduce((sum, s) => sum + sessionMinutes(s), 0);
                        const standName = [...new Set(empSessions.map((s) => s.standName))].join(', ');

                        // rozbicie WŁASNYCH sesji tego pracownika na dni — do rozwijanego
                        // szczegółu "historia odbić i produkcji" per pracownik w tabeli
                        const byEmpDay = {};
                        empSessions.forEach((s) => {
                            const dateStr = dayjs(s.startTime).format('YYYY-MM-DD');
                            (byEmpDay[dateStr] = byEmpDay[dateStr] || []).push(s);
                        });
                        const days = Object.entries(byEmpDay)
                            .map(([dateStr, daySessions]) => ({
                                dateStr,
                                quantity: daySessions.reduce((sum, s) => sum + (s.quantity || 0), 0),
                                sessions: daySessions
                                    .map((s) => ({ startTime: s.startTime, endTime: s.endTime, isOpen: s.isOpen, quantity: s.quantity }))
                                    .sort((a, b) => a.startTime.localeCompare(b.startTime)),
                            }))
                            .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

                        // weeklyUnitsOverNorma/weeklyValue są identyczne na każdej sesji tego
                        // pracownika w tym tygodniu (to suma całego tygodnia) — brane raz, nie
                        // sumowane, inaczej policzylibyśmy premię tygodnia tyle razy, ile sesji.
                        return {
                            employeeId: emp.employeeId,
                            employeeName: emp.employeeName,
                            standName,
                            startTime: minStart.toISOString(),
                            endTime: maxEnd ? maxEnd.toISOString() : null,
                            isOpen: hasOpen,
                            minutes,
                            share: totalMinutesDevice > 0 ? (minutes / totalMinutesDevice) * 100 : 0,
                            production: empSessions.reduce((sum, s) => sum + (s.quantity || 0), 0),
                            overNorm: emp.weeklyUnitsOverNorma || 0,
                            premium: emp.weeklyValue || 0,
                            days,
                        };
                    }).sort((a, b) => b.minutes - a.minutes);

                    // premia maszyny w tym tygodniu = suma premii jej pracowników (nie
                    // sesji — te powtarzają tę samą weeklyValue dla każdej sesji pracownika)
                    const premium = workers.reduce((sum, w) => sum + w.premium, 0);

                    return {
                        deviceId: first.deviceId,
                        deviceName: first.deviceName,
                        unit: display.unit,
                        decimals: display.decimals,
                        production, premium, norma, overNorm, percentOfNorm, workers, dailyBreakdown,
                    };
                }).sort((a, b) => a.deviceName.localeCompare(b.deviceName, 'pl'));

                const first = weekSessions[0];
                // premia jest zawsze w zł, więc sumowanie między maszynami ma sens
                const premium = machines.reduce((sum, m) => sum + m.premium, 0);

                return { weekKey: first.weekKey, weekStart: first.weekStart, weekEnd: first.weekEnd, premium, machines };
            })
            .filter((w) => w.machines.some((m) => m.production > 0.005))
            .sort((a, b) => b.weekStart.localeCompare(a.weekStart));
    }, [sessions, devices]);

    // sumy całego okresu w rozbiciu na maszynę — jeśli w okresie wystąpiła tylko
    // jedna maszyna, to poniżej po prostu zwinie się do jednej liczby z jej jednostką
    const deviceTotals = useMemo(() => {
        const byDevice = {};
        weekGroups.forEach((week) => {
            week.machines.forEach((m) => {
                if (!byDevice[m.deviceId]) {
                    byDevice[m.deviceId] = {
                        deviceId: m.deviceId, deviceName: m.deviceName, unit: m.unit, decimals: m.decimals,
                        production: 0, overNorm: 0, premium: 0,
                    };
                }
                byDevice[m.deviceId].production += m.production;
                byDevice[m.deviceId].overNorm += m.overNorm;
                byDevice[m.deviceId].premium += m.premium;
            });
        });
        return Object.values(byDevice).sort((a, b) => a.deviceName.localeCompare(b.deviceName, 'pl'));
    }, [weekGroups]);

    const singleTotals = deviceTotals.length === 1 ? deviceTotals[0] : null;
    const totalPremium = deviceTotals.reduce((sum, d) => sum + d.premium, 0);
    const weeksWithProduction = weekGroups.length;
    const avgPerWeek = singleTotals && weeksWithProduction > 0 ? singleTotals.production / weeksWithProduction : 0;
    const effectiveRate = singleTotals && singleTotals.overNorm > 0.005 ? singleTotals.premium / singleTotals.overNorm : null;

    const selectedDevice = devices.find((d) => d.id === selectedDeviceId) || null;
    // gdy w okresie nie ma żadnej sesji, deviceTotals jest puste — pokazujemy wtedy
    // jednostkę wybranej/pierwszej maszyny zamiast gołej liczby bez jednostki
    const fallbackDisplay = PRODUCTION_DISPLAY[selectedDevice?.production_source]
        || PRODUCTION_DISPLAY[devices[0]?.production_source]
        || { unit: selectedDevice?.unit || devices[0]?.unit || 'm³', decimals: 1 };
    const soloFmtQty = (v) => {
        const d = singleTotals || fallbackDisplay;
        return `${fmtNum(v, d.decimals)} ${d.unit}`;
    };
    // Norma ma sens tylko dla jednej, konkretnie wybranej maszyny — przy "Wszystkie"
    // maszyny mają różne normy dzienne i ich suma byłaby myląca (patrz norma per-dzień
    // na kartach, tam sumowanie jest poprawne bo dotyczy tylko maszyn użytych danego dnia)
    const topNorma = selectedDevice ? Number(selectedDevice.daily_target) || 0 : null; // devices.daily_target = norma tygodniowa
    const topNormaDisplay = PRODUCTION_DISPLAY[selectedDevice?.production_source]
        || { unit: selectedDevice?.unit || 'm³', decimals: 1 };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                Praca pracowników na maszynach
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    border: '1px solid', borderColor: 'secondary.light', borderRadius: '12px',
                    px: 3, py: 2, mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary' }}>
                        OKRES
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[['week', 'Tydzień'], ['month', 'Miesiąc']].map(([mode, label]) => (
                            <FilterPill key={mode} active={periodMode === mode} onClick={() => setPeriodMode(mode)}>
                                {label}
                            </FilterPill>
                        ))}
                    </Box>

                    <IconButton
                        size="small"
                        onClick={() => setAnchor(shiftAnchor(periodMode, anchor, -1).format('YYYY-MM-DD'))}
                        sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '8px' }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, minWidth: 200, textAlign: 'center', fontSize: '0.9rem' }}>
                        {formatRangeLabel(periodMode, start, end)}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setAnchor(shiftAnchor(periodMode, anchor, 1).format('YYYY-MM-DD'))}
                        sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '8px' }}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.light' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary' }}>
                        MASZYNA
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <FilterPill active={selectedDeviceId === null} onClick={() => setSelectedDeviceId(null)}>
                            Wszystkie
                        </FilterPill>
                        {devices.map((dev) => (
                            <FilterPill key={dev.id} active={selectedDeviceId === dev.id} onClick={() => setSelectedDeviceId(dev.id)}>
                                {dev.name}
                            </FilterPill>
                        ))}
                    </Box>
                </Box>

                {topNorma > 0 && (
                    <Box sx={{ ml: 'auto' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            norma <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {fmtNum(topNorma, topNormaDisplay.decimals)} {topNormaDisplay.unit}
                            </Box> / tydzień
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <StatCard
                    label="Produkcja"
                    value={deviceTotals.length > 1
                        ? <MachineBreakdownList rows={deviceTotals.map((d) => ({ deviceId: d.deviceId, deviceName: d.deviceName, text: `${fmtNum(d.production, d.decimals)} ${d.unit}` }))} />
                        : soloFmtQty(singleTotals?.production || 0)}
                    color="#2C1419"
                    hint={singleTotals && weeksWithProduction > 0 ? `średnio ${soloFmtQty(avgPerWeek)} / tydzień` : null}
                />
                <StatCard
                    label="Ponad normę"
                    value={deviceTotals.length > 1
                        ? <MachineBreakdownList rows={deviceTotals.map((d) => ({ deviceId: d.deviceId, deviceName: d.deviceName, text: `${fmtNum(d.overNorm, d.decimals)} ${d.unit}` }))} />
                        : soloFmtQty(singleTotals?.overNorm || 0)}
                    color={GREEN}
                    hint="premiowana nadwyżka"
                />
                <StatCard
                    label="Premia do wypłaty"
                    value={fmtPln(totalPremium)}
                    color={GREEN}
                    hint={effectiveRate != null ? `stawka śr. ${fmtNum(effectiveRate, 2)} zł/${singleTotals.unit}` : null}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FilterPill active>Po tygodniach</FilterPill>
            </Box>

            {loading ? (
                <Typography sx={{ color: 'text.secondary' }}>Ładowanie…</Typography>
            ) : weekGroups.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'secondary.light', borderRadius: '12px' }}
                >
                    <Typography sx={{ color: 'text.secondary' }}>Brak produkcji w wybranym okresie</Typography>
                </Paper>
            ) : (
                weekGroups.map((week) => (
                    <WeekCard
                        key={week.weekKey}
                        week={week}
                        isOpen={openWeek === week.weekKey}
                        onToggle={() => setOpenWeek((prev) => (prev === week.weekKey ? null : week.weekKey))}
                    />
                ))
            )}
        </Box>
    );
}

export default WorkerOperationReport;
